import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { KEY_VISUALS } from './keyVisuals';
import styles from './styles/KeyBadge.module.css';

/**
 * The bare coloured icon for a key role. Used on the canvas, in edge labels and
 * in the legend, where a full chip would not fit.
 *
 * @param {{role: 'grant'|'has'|'lacks', size?: number, title?: string, className?: string}} props
 */
export const KeyRoleIcon = ({ role, size = 12, title, className, color }) => {
    const visual = KEY_VISUALS[role];
    if (!visual) return null;
    const { Icon } = visual;
    return (
        <Icon
            size={size}
            color={color || visual.color}
            className={className}
            title={title}
            aria-hidden={title ? undefined : true}
            style={{ flex: '0 0 auto' }}
        />
    );
};

/**
 * A key, as a pill: role icon, name, and optionally a remove button.
 *
 * Presentational only — no editor context — so the preview player can use it
 * too. A caller that wants hovering to spotlight the key on the canvas passes
 * `onHoverChange`.
 *
 * @param {{
 *   role: 'grant'|'has'|'lacks',
 *   name: string,
 *   onRemove?: () => void,
 *   onClick?: () => void,
 *   onHoverChange?: (hovering: boolean) => void,
 *   size?: 'sm'|'md',
 * }} props
 */
const KeyBadge = ({ role, name, onRemove, onClick, onHoverChange, size = 'md' }) => {
    const visual = KEY_VISUALS[role] || KEY_VISUALS.grant;
    const hoverProps = onHoverChange
        ? {
            onMouseEnter: () => onHoverChange(true),
            onMouseLeave: () => onHoverChange(false),
        }
        : {};

    const content = (
        <>
            <KeyRoleIcon role={role} size={size === 'sm' ? 10 : 12} />
            <span className={styles.name}>{name}</span>
        </>
    );

    return (
        <span
            className={`${styles.badge} ${size === 'sm' ? styles.sm : styles.md}`}
            style={{ '--role-color': visual.color, '--role-bg': visual.bg }}
            data-role={role}
            {...hoverProps}
        >
            {onClick ? (
                <button type="button" className={styles.mainButton} onClick={onClick}>
                    {content}
                </button>
            ) : (
                content
            )}
            {onRemove && (
                <button
                    type="button"
                    className={styles.remove}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (onHoverChange) onHoverChange(false);
                        onRemove();
                    }}
                    aria-label={`Remove ${name}`}
                    title={`Remove ${name}`}
                >
                    <FaTimes size={10} />
                </button>
            )}
        </span>
    );
};

export default KeyBadge;
